import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  MapPin, Clock, User, Phone, Mail, ExternalLink, Download, FileText,
  AlertTriangle, ShieldCheck, TrendingUp, Users2, ArrowLeft, Loader2,
} from "lucide-react";
import api, { API } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { VerificationBadge } from "@/components/VerificationBadge";
import { CauseChip } from "@/components/NGOCard";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const CRED_LABELS = { DARPAN: "DARPAN ID", CSR1: "CSR-1 (MCA)", "12A": "12A", "80G": "80G", "10_23C": "10(23C)", FCRA: "FCRA" };

export default function OrgProfile() {
  const { id } = useParams();
  const { t } = useT();
  const { user } = useAuth();
  const [org, setOrg] = useState(null);
  const [contact, setContact] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/orgs/${id}`).then((r) => setOrg(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  const revealContact = async () => {
    if (!user) { toast.error("Please log in to view direct contact details"); return; }
    try {
      const { data } = await api.post(`/orgs/${id}/contact`, { channel: "reveal" });
      setContact(data); setContactOpen(true);
    } catch (e) { toast.error("Could not load contact details"); }
  };

  const downloadPack = async () => {
    if (!user) { toast.error("Log in as a CSR manager to download the due-diligence pack"); return; }
    setPdfLoading(true);
    try {
      const res = await api.get(`/orgs/${id}/due-diligence`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `due-diligence-${org.display_name}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Due-diligence pack downloaded");
    } catch (e) { toast.error("Could not generate the pack"); } finally { setPdfLoading(false); }
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-stone-500">{t("common.loading")}</div>;
  if (!org) return <div className="min-h-[60vh] flex items-center justify-center text-stone-500">Organisation not found.</div>;

  const alerts = org.credential_alerts || [];

  return (
    <div>
      {/* HERO */}
      <div className="relative h-64 md:h-80">
        <img src={org.hero_image} alt={org.display_name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-brand/90 to-slate-brand/20" />
        <div className="absolute inset-x-0 bottom-0 max-w-7xl mx-auto px-4 md:px-8 pb-6">
          <Link to="/discover" className="inline-flex items-center gap-1 text-stone-200 text-sm mb-3 hover:text-white"><ArrowLeft size={15} /> {t("nav.discover")}</Link>
          <div className="flex items-center gap-3 flex-wrap mb-2">
            <VerificationBadge tier={org.verification_tier} size="lg" />
            {org.csr_eligible ? (
              <span className="inline-flex items-center gap-1 text-xs font-semibold bg-tier2-bg text-tier2-text border border-tier2-border rounded-full px-2.5 py-1">{t("eligible.yes")}</span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs font-semibold bg-tier1-bg text-tier1-text border border-tier1-border rounded-full px-2.5 py-1">{t("eligible.no")}</span>
            )}
          </div>
          <h1 className="text-2xl md:text-4xl font-extrabold text-white">{org.display_name}</h1>
          <p className="text-stone-200 mt-1 flex items-center gap-1.5 text-sm"><MapPin size={15} /> {org.district}, {org.state} · {org.reg_type}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 grid lg:grid-cols-3 gap-8">
        {/* MAIN */}
        <div className="lg:col-span-2 space-y-8">
          <div className="flex flex-wrap gap-3">
            <Button onClick={revealContact} className="bg-terracotta hover:bg-terracotta-hover" data-testid="contact-directly-btn">
              <Phone size={16} className="mr-1.5" /> {t("common.contact")}
            </Button>
            <Button onClick={downloadPack} disabled={pdfLoading} variant="outline" className="border-slate-brand text-slate-brand" data-testid="download-due-diligence-pdf-button">
              {pdfLoading ? <Loader2 size={16} className="mr-1.5 animate-spin" /> : <Download size={16} className="mr-1.5" />} {t("common.download_pack")}
            </Button>
          </div>

          {alerts.some((a) => a.state === "lapsed") && (
            <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800" data-testid="credential-lapsed-notice">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <span>Credential lapsed: {alerts.filter((a) => a.state === "lapsed").map((a) => a.type).join(", ")}. Verify current status before partnering.</span>
            </div>
          )}

          <section>
            <h2 className="text-xl font-bold text-slate-brand mb-2">Mission</h2>
            <p className="text-stone-700 measure">{org.mission}</p>
            {org.mission_ta && <p className="font-tamil text-stone-600 mt-2 measure">{org.mission_ta}</p>}
            <p className="text-stone-700 mt-4 measure">{org.story}</p>
          </section>

          {/* Impact */}
          {org.impact_numbers?.length > 0 && (
            <section className="grid grid-cols-3 gap-4">
              {org.impact_numbers.map((im, i) => (
                <div key={i} className="bg-white rounded-xl border border-stone-200 p-4 text-center">
                  <div className="text-2xl font-extrabold text-terracotta">{im.value}</div>
                  <div className="text-xs text-stone-500 mt-1">{im.label}</div>
                </div>
              ))}
            </section>
          )}

          {/* Gallery */}
          {org.gallery?.length > 0 && (
            <section>
              <h2 className="text-xl font-bold text-slate-brand mb-3">Photo gallery</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {org.gallery.map((g, i) => (
                  <img key={i} src={g} alt={`${org.display_name} ${i + 1}`} loading="lazy" className="aspect-square object-cover rounded-lg border border-stone-200" />
                ))}
              </div>
            </section>
          )}

          {/* Projects */}
          {org.projects?.length > 0 && (
            <section>
              <h2 className="text-xl font-bold text-slate-brand mb-3">Active projects</h2>
              <div className="space-y-3">
                {org.projects.map((p) => (
                  <div key={p.id} className="bg-white rounded-xl border border-stone-200 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-semibold text-stone-900">{p.title}</h3>
                      <span className="text-xs bg-stone-100 rounded px-2 py-0.5 text-stone-600 shrink-0">Sch. VII ({(p.schedule_vii_codes || []).join(", ")})</span>
                    </div>
                    <p className="text-sm text-stone-600 mt-1">{p.description}</p>
                    <div className="flex flex-wrap gap-2 mt-2 text-xs text-stone-500">
                      <span className="flex items-center gap-1"><Users2 size={13} /> {p.beneficiary_count?.toLocaleString()} beneficiaries</span>
                      <span className="flex items-center gap-1"><MapPin size={13} /> {p.district}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Financials */}
          {org.financials?.length > 0 && (
            <section>
              <h2 className="text-xl font-bold text-slate-brand mb-3 flex items-center gap-2"><TrendingUp size={18} /> Financials (program vs admin)</h2>
              <div className="overflow-x-auto rounded-xl border border-stone-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-brand text-white">
                    <tr><th className="text-left p-3">FY</th><th className="text-right p-3">Income</th><th className="text-right p-3">Program</th><th className="text-right p-3">Admin</th><th className="text-right p-3">Program %</th></tr>
                  </thead>
                  <tbody>
                    {org.financials.map((fin) => {
                      const pct = fin.total_income ? Math.round(fin.program_expense / fin.total_income * 100) : 0;
                      return (
                        <tr key={fin.fy} className="border-t border-stone-100">
                          <td className="p-3 font-medium">{fin.fy}</td>
                          <td className="p-3 text-right">₹{fin.total_income.toLocaleString()}</td>
                          <td className="p-3 text-right">₹{fin.program_expense.toLocaleString()}</td>
                          <td className="p-3 text-right">₹{fin.admin_expense.toLocaleString()}</td>
                          <td className="p-3 text-right font-semibold text-tier3-text">{pct}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Team */}
          {org.team?.length > 0 && (
            <section>
              <h2 className="text-xl font-bold text-slate-brand mb-3">Governance & trustees</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {org.team.map((p, i) => (
                  <div key={i} className="bg-white rounded-lg border border-stone-200 p-3 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-terracotta-light text-terracotta flex items-center justify-center font-semibold">{p.name[0]}</div>
                    <div><div className="font-medium text-stone-900 text-sm">{p.name}</div><div className="text-xs text-stone-500">{p.role}{p.is_trustee ? " · Trustee" : ""}</div></div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* SIDEBAR */}
        <aside className="space-y-6">
          {/* Credentials */}
          <div className="bg-white rounded-xl border border-stone-200 p-5">
            <h3 className="font-bold text-slate-brand mb-3 flex items-center gap-2"><ShieldCheck size={18} className="text-tier2-icon" /> Credentials</h3>
            <div className="space-y-2">
              {(org.credentials || []).map((c) => {
                const lapsed = alerts.find((a) => a.type === c.type && a.state === "lapsed");
                const expiring = alerts.find((a) => a.type === c.type && a.state === "expiring");
                return (
                  <div key={c.id} className="flex items-center justify-between text-sm border-b border-stone-100 pb-2 last:border-0">
                    <div>
                      <div className="font-medium text-stone-800">{CRED_LABELS[c.type] || c.type}</div>
                      <div className="text-xs text-stone-500 font-mono">{c.number || "—"}</div>
                    </div>
                    <div className="text-right text-xs">
                      {lapsed ? <span className="text-red-600 font-semibold">Lapsed</span>
                        : expiring ? <span className="text-tier1-text font-semibold">Expires in {expiring.days}d</span>
                        : c.expiry_date ? <span className="text-stone-500">till {c.expiry_date}</span>
                        : <span className="text-tier3-text">Valid</span>}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-stone-400 mt-3">DARPAN: {org.darpan_id}</p>
          </div>

          {/* How to visit */}
          <div className="bg-white rounded-xl border border-stone-200 p-5">
            <h3 className="font-bold text-slate-brand mb-3">How to visit</h3>
            <ul className="space-y-2.5 text-sm text-stone-700">
              <li className="flex items-start gap-2"><MapPin size={15} className="text-terracotta shrink-0 mt-0.5" /> {org.contact_public?.address}</li>
              <li className="flex items-start gap-2"><Clock size={15} className="text-terracotta shrink-0 mt-0.5" /> {org.contact_public?.visiting_hours}</li>
              <li className="flex items-start gap-2"><User size={15} className="text-terracotta shrink-0 mt-0.5" /> {org.contact_public?.person}</li>
            </ul>
            <a href={`https://www.openstreetmap.org/?mlat=${org.lat}&mlon=${org.lng}#map=15/${org.lat}/${org.lng}`} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-terracotta font-semibold text-sm mt-3" data-testid="directions-link">
              <ExternalLink size={14} /> Get directions
            </a>
          </div>

          <div className="bg-terracotta-light rounded-xl border border-terracotta-border p-5 text-sm text-stone-700">
            <p className="font-semibold text-slate-brand mb-1">No money moves through The Great Indian Dream.</p>
            <p>You contact and give directly to this organisation. Your 80G receipt comes from them.</p>
          </div>
        </aside>
      </div>

      {/* Contact dialog */}
      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent data-testid="contact-dialog">
          <DialogHeader>
            <DialogTitle>Contact {org.display_name} directly</DialogTitle>
            <DialogDescription>You now leave the platform to connect with them. The Great Indian Dream takes no commission and never routes your donation.</DialogDescription>
          </DialogHeader>
          {contact && (
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2"><User size={15} className="text-terracotta" /> {contact.person}</div>
              <div className="flex items-center gap-2"><Phone size={15} className="text-terracotta" /> <a href={`tel:${contact.phone}`} className="text-slate-brand font-medium">{contact.phone}</a></div>
              <div className="flex items-center gap-2"><Mail size={15} className="text-terracotta" /> <a href={`mailto:${contact.email}`} className="text-slate-brand font-medium">{contact.email}</a></div>
              {contact.donation_url && (
                <a href={contact.donation_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-terracotta font-semibold mt-2" data-testid="external-donation-link">
                  <ExternalLink size={15} /> Their own donation page (leaves Thadam)
                </a>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
